import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ColorPickerProps {
  color: string;
  onColorChange: (color: string) => void;
  showWheel?: boolean;
  showSwatches?: boolean;
  swatches?: Array<{ name: string; value: string }>;
  label?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onColorChange,
  showWheel = true,
  showSwatches = true,
  swatches = [],
  label
}) => {
  const { t } = useTranslation();
  const [isWheelOpen, setIsWheelOpen] = useState(false);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // 색상을 HSL로 변환하는 함수
  const hexToHSL = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  // HSL을 HEX로 변환하는 함수
  const hslToHex = (h: number, s: number, l: number) => {
    h /= 360;
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h * 6) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 1/6) {
      r = c; g = x; b = 0;
    } else if (1/6 <= h && h < 1/3) {
      r = x; g = c; b = 0;
    } else if (1/3 <= h && h < 1/2) {
      r = 0; g = c; b = x;
    } else if (1/2 <= h && h < 2/3) {
      r = 0; g = x; b = c;
    } else if (2/3 <= h && h < 5/6) {
      r = x; g = 0; b = c;
    } else if (5/6 <= h && h <= 1) {
      r = c; g = 0; b = x;
    }

    const rHex = Math.round((r + m) * 255).toString(16).padStart(2, '0');
    const gHex = Math.round((g + m) * 255).toString(16).padStart(2, '0');
    const bHex = Math.round((b + m) * 255).toString(16).padStart(2, '0');

    return `#${rHex}${gHex}${bHex}`;
  };

  // 초기 색상 설정 (외부에서 색상이 변경될 때만)
  useEffect(() => {
    const hsl = hexToHSL(color);
    setHue(hsl.h);
    setSaturation(hsl.s);
    setLightness(hsl.l);
  }, [color]);

  // HSL 슬라이더 변경 핸들러
  const handleHueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHue = parseInt(e.target.value);
    setHue(newHue);
    const newColor = hslToHex(newHue, saturation, lightness);
    onColorChange(newColor);
  };

  const handleSaturationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSaturation = parseInt(e.target.value);
    setSaturation(newSaturation);
    const newColor = hslToHex(hue, newSaturation, lightness);
    onColorChange(newColor);
  };

  const handleLightnessChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLightness = parseInt(e.target.value);
    setLightness(newLightness);
    const newColor = hslToHex(hue, saturation, newLightness);
    onColorChange(newColor);
  };

  const handleColorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onColorChange(e.target.value);
  };

  const handleSwatchClick = (swatchColor: string) => {
    onColorChange(swatchColor);
  };

  return (
    <div className="color-picker-container">
      {label && (
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* 색상 스와치 */}
      {showSwatches && swatches.length > 0 && (
        <div className="mb-4">
          <div className="grid grid-cols-5 gap-2">
            {swatches.map((swatch) => (
              <button
                key={swatch.value}
                type="button"
                onClick={() => handleSwatchClick(swatch.value)}
                className={`w-10 h-10 rounded-full border-2 transition-all ${
                  color === swatch.value
                    ? 'border-blue-500 scale-110'
                    : 'border-gray-300 hover:scale-105'
                }`}
                style={{ backgroundColor: swatch.value }}
                title={swatch.name}
              />
            ))}
          </div>
        </div>
      )}

      {/* HTML5 컬러 픽커 */}
      <div className="mb-4">
        <div className="flex items-center space-x-3">
          <input
            ref={colorInputRef}
            type="color"
            value={color}
            onChange={handleColorInputChange}
            className="w-12 h-12 rounded border-2 border-gray-300 cursor-pointer"
          />
          <span className="text-sm text-gray-600 font-mono">{color}</span>
        </div>
      </div>

      {/* 커스텀 휠 컬러 픽커 */}
      {showWheel && (
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setIsWheelOpen(!isWheelOpen)}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            {isWheelOpen ? t('common.close', 'Close') : t('cardColorSelection', 'Color Selection')}
          </button>

          {isWheelOpen && (
            <div className="mt-3 p-4 bg-white border border-gray-200 rounded-lg shadow-lg">
              {/* 색조 슬라이더 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('colorPicker.hue', 'Hue')}
                </label>
                <div className="relative">
                                     <input
                     type="range"
                     min="0"
                     max="360"
                     value={hue}
                     onChange={handleHueChange}
                     className="w-full h-2 bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 via-purple-500 to-red-500 rounded-lg appearance-none cursor-pointer"
                     style={{
                       background: `linear-gradient(to right, 
                         hsl(0, 100%, 50%), 
                         hsl(60, 100%, 50%), 
                         hsl(120, 100%, 50%), 
                         hsl(180, 100%, 50%), 
                         hsl(240, 100%, 50%), 
                         hsl(300, 100%, 50%), 
                         hsl(360, 100%, 50%)
                       )`
                     }}
                   />
                  <div className="text-xs text-gray-500 mt-1">{hue}°</div>
                </div>
              </div>

              {/* 채도 슬라이더 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('colorPicker.saturation', 'Saturation')}
                </label>
                <div className="relative">
                                     <input
                     type="range"
                     min="0"
                     max="100"
                     value={saturation}
                     onChange={handleSaturationChange}
                     className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                     style={{
                       background: `linear-gradient(to right, 
                         hsl(${hue}, 0%, ${lightness}%), 
                         hsl(${hue}, 100%, ${lightness}%)
                       )`
                     }}
                   />
                  <div className="text-xs text-gray-500 mt-1">{saturation}%</div>
                </div>
              </div>

              {/* 명도 슬라이더 */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                                     {t('colorPicker.lightness', 'Lightness')}
                </label>
                <div className="relative">
                                     <input
                     type="range"
                     min="0"
                     max="100"
                     value={lightness}
                     onChange={handleLightnessChange}
                     className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                     style={{
                       background: `linear-gradient(to right, 
                         hsl(${hue}, ${saturation}%, 0%), 
                         hsl(${hue}, ${saturation}%, 50%), 
                         hsl(${hue}, ${saturation}%, 100%)
                       )`
                     }}
                   />
                  <div className="text-xs text-gray-500 mt-1">{lightness}%</div>
                </div>
              </div>

              {/* 현재 색상 미리보기 */}
              <div className="flex items-center justify-center p-3 bg-gray-50 rounded-md">
                <div
                  className="w-16 h-16 rounded-lg border-2 border-gray-300"
                  style={{ backgroundColor: color }}
                />
                <div className="ml-3 text-sm">
                  <div className="font-mono">{color}</div>
                  <div className="text-gray-500">
                    HSL({hue}, {saturation}%, {lightness}%)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
